// false positive no-unnecessary-type-assertion
// https://typescript-eslint.io/rules/no-unnecessary-condition/#possibly-undefined-indexed-access
import { useMemo } from 'react';
import type { Serie, SubjectInfo } from '../../../studio-blocks/components/charts/types';
import { compact, zip } from 'lodash';
import { assertNotNil, type CustomizableMetric, getAnalyzedSubjectFromRequestSubject } from 'venn-utils';
import { getSaturationLevel } from '../../../studio-blocks/components/blocks/public-private/utils';
import { useConfidenceLevels } from '../../../studio-blocks/logic/useConfidenceLevels';
import { useSubjectColors } from '../../../studio-blocks/logic/useSubjectColors';
import { allSubjectsAsOfDates, type StudioAnalysisRequest } from 'venn-state';
import type { Analysis, ReturnCell } from 'venn-api';
import { useRecoilValue } from 'recoil';
import { useBlockId } from '../../../studio-blocks';
import { toTypeUnsafeSubject } from '../../../studio-blocks/logic/exportUtils';

type ExportableSeries = { subject: SubjectInfo; series: Serie[] }[];

export const useGrowthSimulationSeries = (
  requests: StudioAnalysisRequest[],
  analyses: (Analysis | undefined)[][],
  metrics: CustomizableMetric[],
): [series: Serie[], exportableSeries: ExportableSeries, subjectInfo: SubjectInfo[]] => {
  const blockId = useBlockId();
  const request = requests[0]!;
  const confidenceLevels = useConfidenceLevels();

  const analyzedSubjects = useMemo(
    () => requests.map(({ subject }) => getAnalyzedSubjectFromRequestSubject(subject)),
    [requests],
  );
  const subjectColors = useSubjectColors(analyzedSubjects);

  const [series, exportableSeries, subjectInfo] = useMemo(() => {
    const series: Serie[] = [];
    const exportableSeries: { subject: SubjectInfo; series: Serie[] }[] = [];
    const subjectInfo: SubjectInfo[] = [];
    const metric = metrics ? metrics[0] : undefined;

    if (!analyses || analyses.length < 1 || !metric || !requests) {
      return [series, exportableSeries, subjectInfo];
    }

    const analysis = analyses[0]![0]!;
    if (!analysis || !analysis.growthSimulation) {
      return [series, exportableSeries, subjectInfo];
    }

    if (compact(analysis.growthSimulation).length !== analysis.growthSimulation.length) {
      // check that growthsimulations does not contain any null values
      return [series, exportableSeries, subjectInfo];
    }

    analysis.growthSimulation.forEach((simulation, subIdx) => {
      const analysisRequest = requests[subIdx];
      const subject = !analysisRequest!
        ? subIdx === 0
          ? request.subject
          : assertNotNil(request.benchmark)
        : requests[subIdx]!.subject;

      const isBenchmark = subIdx !== 0 || analysisRequest?.subject?.fund?.assetType === 'BENCHMARK';

      subjectInfo.push({
        subject: { ...subject, isBenchmark },
        key: subIdx,
        return: simulation.forecastReturn,
        volitility: simulation.forecastVolatility,
      });

      const subjectSeries: Serie[] = [];
      confidenceLevels.forEach((confidenceLevel) => {
        const growthSimulation = simulation.growthSimulations[confidenceLevel.originalIndex];
        if (!growthSimulation || !confidenceLevel.visible) {
          return;
        }
        const ts = growthSimulation.forecast;
        const serie = {
          name: `${String(Math.round(growthSimulation.confidenceLevel * 100))}%`,
          color: getSaturationLevel(subjectColors[subIdx]!, growthSimulation.confidenceLevel),
          data: convertToTimeseries(ts),
          key: subIdx,
        };

        subjectSeries.push(serie);

        if (confidenceLevel.visible) {
          series.push(serie);
        }
      });

      if (isExportable(analysis.exportable ?? [], subIdx)) {
        exportableSeries.push({ subject: subjectInfo[subIdx]!, series: subjectSeries });
      }
    });

    return [series, exportableSeries, subjectInfo];
  }, [metrics, analyses, requests, request.subject, request.benchmark, confidenceLevels, subjectColors]);
  const allAsOfDates = useRecoilValue(
    allSubjectsAsOfDates(exportableSeries.map(({ subject }) => [blockId, toTypeUnsafeSubject(subject.subject)])),
  ).map(({ asOfDate }) => asOfDate);
  const exportableSeriesWithAsOfDates: ExportableSeries = zip(exportableSeries, allAsOfDates).map(
    ([serie, asOfDate]) => ({
      // serie is always non nil because exportableSeries and allAsOfDates are always the same length
      ...serie!,
      subject: {
        ...serie!.subject,
        asOfDate,
      },
    }),
  );
  return [series, exportableSeriesWithAsOfDates, subjectInfo];
};

const isExportable = (exportable: boolean[], index: number) => {
  if (index === -1) {
    return false;
  }
  return exportable[index];
};
const convertToTimeseries = (ts: number[][] | ReturnCell[]): number[][] => {
  if (!ts) {
    return [];
  }
  if (Array.isArray(ts[0])) {
    return ts as number[][];
  }

  return (ts as ReturnCell[]).map((t) => [t.date, t.val]);
};
